import React, { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { NuqsAdapter } from 'nuqs/adapters/react'
import RouteFallback from './RouteFallback'
import RouteChunkBoundary from './RouteChunkBoundary'

// Eager: the app shell and the cold-start entry point. Making the login page
// lazy would cost an extra round-trip on the one screen every user starts from.
import LoginPage from '@/app/pages/LoginPage'
import MainLayout from '@/app/components/layout/MainLayout'
import ProtectedRoute from './ProtectedRoute'

// Everything below is code-split: a page's chunk is fetched the first time its
// route is visited, so a user who only ever opens invoices never downloads the
// reporting dashboards, PDF templates or the pricing engine.
const DashboardPage = lazy(() => import('@/app/pages/dashboard/page'))
const OrganizationsPage = lazy(() => import('@/app/pages/OrganizationsPage'))
const SitesPage = lazy(() => import('@/app/pages/SitesPage'))
const PartyPage = lazy(() => import('@/app/pages/party/page'))
const PartyDetailPage = lazy(() => import('@/app/pages/party/detail/page'))
const LeadsPage = lazy(() => import('@/app/pages/leads/page'))
const LeadDetailPage = lazy(() => import('@/app/pages/leads/detail/page'))
const ItemsPage = lazy(() => import('@/app/pages/items/page'))
const ItemDetailPage = lazy(() => import('@/app/pages/items/detail/page'))

// Settings Pages
const UnitsPage = lazy(() => import('@/app/pages/settings/units/page'))
const HsnCodesPage = lazy(() => import('@/app/pages/settings/hsn-codes/page'))
const TaxRatesPage = lazy(() => import('@/app/pages/settings/tax-rates/page'))
const CategoriesPage = lazy(() => import('@/app/pages/settings/categories/page'))
const CategoryDetailPage = lazy(() => import('@/app/pages/settings/categories/detail/page'))
const OrganizationSettingsPage = lazy(
  () => import('@/app/pages/settings/organization-settings/page'),
)
const NotificationsPage = lazy(() => import('@/app/pages/settings/notifications/page'))

// Accounting Pages
const ChartOfAccountsPage = lazy(() => import('@/app/pages/accounting/chart-of-accounts/page'))
const AccountStatementPage = lazy(() => import('@/app/pages/accounting/account-statement/page'))

// Inventory Pages
const InventoryPage = lazy(() => import('@/app/pages/inventory/page'))
const BulkStockReconcilePage = lazy(() => import('@/app/pages/inventory/bulk-reconcile/page'))

// Pricing Pages
const PricingPage = lazy(() => import('@/app/pages/pricing/page'))
const BulkPriceUpdatePage = lazy(() => import('@/app/pages/pricing/bulk-update/page'))
const SiteAvailabilityPage = lazy(() => import('@/app/pages/pricing/site-availability/page'))

// Transactions Pages
const VoucherTypesPage = lazy(() => import('@/app/pages/transactions/voucher-types/page'))
const VouchersPage = lazy(() => import('@/app/pages/transactions/vouchers/page'))
const VoucherDetailPage = lazy(() => import('@/app/pages/transactions/vouchers/detail/page'))

// Sales Order Pages
const SalesOrdersPage = lazy(() => import('@/app/pages/sales-orders/list/page'))
const SalesOrderDetailPage = lazy(() => import('@/app/pages/sales-orders/detail/page'))

// Sales Invoice Pages
const SalesInvoicesPage = lazy(() => import('@/app/pages/sales-invoices/list/page'))
const SalesInvoiceDetailPage = lazy(() => import('@/app/pages/sales-invoices/detail/page'))

// Quotation Pages
const QuotationsPage = lazy(() => import('@/app/pages/quotations/list/page'))
const QuotationDetailPage = lazy(() => import('@/app/pages/quotations/detail/page'))

// Expense Pages
const ExpenseListPage = lazy(() => import('@/app/pages/expenses/list/page'))
const ExpenseDetailPage = lazy(() => import('@/app/pages/expenses/detail/page'))

// Payments Pages
const PaymentsInPage = lazy(() => import('@/app/pages/payments-in/page'))
const PaymentsInListPage = lazy(() => import('@/app/pages/payments-in/list/page'))
const PaymentsInDetailPage = lazy(() => import('@/app/pages/payments-in/detail/page'))
const PaymentsOutPage = lazy(() => import('@/app/pages/payments-out/page'))
const PaymentsOutListPage = lazy(() => import('@/app/pages/payments-out/list/page'))
const PaymentsOutDetailPage = lazy(() => import('@/app/pages/payments-out/detail/page'))

// Cheque Pages
const ChequesListPage = lazy(() => import('@/app/pages/cheques/list/page'))
const RecordChequePage = lazy(() => import('@/app/pages/cheques/new/page'))

// Purchase Order Pages
const PurchaseOrdersPage = lazy(() => import('@/app/pages/purchase-orders/list/page'))
const PurchaseOrderDetailPage = lazy(() => import('@/app/pages/purchase-orders/detail/page'))

// Purchase Invoice Pages
const PurchaseInvoicesPage = lazy(() => import('@/app/pages/purchase-invoices/list/page'))
const PurchaseInvoiceDetailPage = lazy(() => import('@/app/pages/purchase-invoices/detail/page'))

// Credit Note Pages
const CreditNotesPage = lazy(() => import('@/app/pages/credit-notes/list/page'))
const CreditNoteDetailPage = lazy(() => import('@/app/pages/credit-notes/detail/page'))

// Debit Note Pages
const DebitNotesPage = lazy(() => import('@/app/pages/debit-notes/list/page'))
const DebitNoteDetailPage = lazy(() => import('@/app/pages/debit-notes/detail/page'))

// Offline Invoice Pages
const OfflineInvoicesPage = lazy(() => import('@/app/pages/offline-invoices/page'))
const OfflineInvoiceListPage = lazy(() => import('@/app/pages/offline-invoices/list/page'))
const OfflineInvoiceCreatePage = lazy(() => import('@/app/pages/offline-invoices/create/page'))

// Day-End Closure Page
const DayEndClosurePage = lazy(() => import('@/app/pages/day-end-closure/page'))

// Field Agent Cash Page
const FieldAgentCashPage = lazy(() => import('@/app/pages/field-agent-cash/page'))

// Razorpay Transactions Page
const RazorpayTransactionsPage = lazy(() => import('@/app/pages/razorpay-transactions/page'))

// Reporting Pages
const PartiesReportPage = lazy(() => import('@/app/pages/admin/reporting/parties/page'))
const DeliveryMetricsPage = lazy(() => import('@/app/pages/admin/reporting/delivery/page'))
const CustomerMetricsPage = lazy(() => import('@/app/pages/admin/reporting/customers/page'))
const SalesReportPage = lazy(() => import('@/app/pages/admin/reporting/sales/page'))
const InventoryReportPage = lazy(() => import('@/app/pages/admin/reporting/inventory/page'))
const SalesAnalysisPage = lazy(() => import('@/app/pages/admin/reporting/sales-analysis/page'))
const WinbackReportPage = lazy(() => import('@/app/pages/admin/reporting/winback/page'))

// Internal Transfer Pages
const InternalTransfersListPage = lazy(
  () => import('@/app/pages/items/internal-transfers/list/page'),
)
const InternalTransfersDetailPage = lazy(
  () => import('@/app/pages/items/internal-transfers/detail/page'),
)

// Low Stock Report Page
const LowStockReportPage = lazy(() => import('@/app/pages/items/low-stock-report/page'))

// Item Collections Pages
const ItemCollectionsListPage = lazy(() => import('@/app/pages/items/collections/list/page'))
const ItemCollectionDetailPage = lazy(() => import('@/app/pages/items/collections/detail/page'))

/**
 * Sits between MainLayout and the pages so the sidebar and header stay mounted
 * while the next page's chunk downloads — only the content area shows the
 * skeleton. The error boundary wraps Suspense (not the other way round) so it
 * catches the rejected import rather than the pending one.
 */
const SuspendedOutlet: React.FC = () => (
  <RouteChunkBoundary>
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  </RouteChunkBoundary>
)

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <Routes>
          {/* Login Route */}
          <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route element={<SuspendedOutlet />}>
            {/* Default route after login */}
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/organizations" element={<OrganizationsPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/party/detail/:id" element={<PartyDetailPage />} />
            <Route path="/party" element={<PartyPage />} />
            {/* detail before the bare route, matching the convention above */}
            <Route path="/leads/detail/:id" element={<LeadDetailPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/items/detail/:id" element={<ItemDetailPage />} />
            <Route path="/items/detail" element={<ItemDetailPage />} />

            {/* Internal Transfer Routes */}
            <Route path="/items/internal-transfers" element={<InternalTransfersListPage />} />
            <Route path="/items/internal-transfers/detail/:id" element={<InternalTransfersDetailPage />} />
            <Route path="/items/internal-transfers/detail" element={<InternalTransfersDetailPage />} />

            {/* Low Stock Report Route */}
            <Route path="/items/low-stock-report" element={<LowStockReportPage />} />

            {/* Item Collections Routes */}
            <Route path="/items/collections" element={<ItemCollectionsListPage />} />
            <Route path="/items/collections/detail/:id" element={<ItemCollectionDetailPage />} />
            <Route path="/items/collections/detail" element={<ItemCollectionDetailPage />} />

            {/* Settings Routes */}
            <Route path="/settings/units" element={<UnitsPage />} />
            <Route path="/settings/hsn-codes" element={<HsnCodesPage />} />
            <Route path="/settings/tax-rates" element={<TaxRatesPage />} />
            <Route path="/settings/categories" element={<CategoriesPage />} />
            <Route path="/settings/categories/detail/:id" element={<CategoryDetailPage />} />
            <Route path="/settings/organization-settings" element={<OrganizationSettingsPage />} />
            <Route path="/settings/notifications" element={<NotificationsPage />} />

            {/* Accounting Routes */}
            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="/accounting/account-statement" element={<AccountStatementPage />} />

            {/* Inventory Routes */}
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/inventory/bulk-reconcile" element={<BulkStockReconcilePage />} />

            {/* Pricing Route */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/bulk-update" element={<BulkPriceUpdatePage />} />
            <Route path="/pricing/site-availability" element={<SiteAvailabilityPage />} />

            {/* Transactions Routes */}
            <Route path="/voucher-types" element={<VoucherTypesPage />} />
            <Route path="/vouchers" element={<VouchersPage />} />
            <Route path="/transactions/vouchers/detail/:id" element={<VoucherDetailPage />} />

            {/* Sales Order Routes - Keep detail/:id route before the detail route */}
            <Route path="/sales-orders/detail/:id" element={<SalesOrderDetailPage />} />
            <Route path="/sales-orders/detail" element={<SalesOrderDetailPage />} />
            <Route path="/sales-orders/list" element={<SalesOrdersPage />} />
            <Route path="/sales-orders" element={<Navigate to="/sales-orders/list" replace />} />

            {/* Sales Invoice Routes */}
            <Route path="/sales-invoices/detail/:id" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/detail" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/new" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales-invoices/list" element={<SalesInvoicesPage />} />
            <Route
              path="/sales-invoices"
              element={<Navigate to="/sales-invoices/list" replace />}
            />

            {/* Quotation Routes */}
            <Route path="/quotations/detail/:id" element={<QuotationDetailPage />} />
            <Route path="/quotations/detail" element={<QuotationDetailPage />} />
            <Route path="/quotations/list" element={<QuotationsPage />} />
            <Route path="/quotations" element={<Navigate to="/quotations/list" replace />} />

            {/* Expense Routes */}
            <Route path="/expenses/list" element={<ExpenseListPage />} />
            <Route path="/expenses/detail" element={<ExpenseDetailPage />} />
            <Route path="/expenses/detail/:id" element={<ExpenseDetailPage />} />

            {/* Payments Routes */}
            <Route path="/payments-in" element={<PaymentsInPage />} />
            <Route path="/payments-in/list" element={<PaymentsInListPage />} />
            <Route path="/payments-in/detail/:id" element={<PaymentsInDetailPage />} />

            <Route path="/payments-out" element={<PaymentsOutPage />} />
            <Route path="/payments-out/list" element={<PaymentsOutListPage />} />
            <Route path="/payments-out/detail/:id" element={<PaymentsOutDetailPage />} />

            {/* Cheque Routes */}
            <Route path="/cheques/list" element={<ChequesListPage />} />
            <Route path="/cheques/new" element={<RecordChequePage />} />
            <Route path="/cheques" element={<Navigate to="/cheques/list" replace />} />

            {/* Purchase Order Routes */}
            <Route path="/purchase-orders/detail/:id" element={<PurchaseOrderDetailPage />} />
            <Route path="/purchase-orders/detail" element={<PurchaseOrderDetailPage />} />
            <Route path="/purchase-orders/list" element={<PurchaseOrdersPage />} />
            <Route
              path="/purchase-orders"
              element={<Navigate to="/purchase-orders/list" replace />}
            />

            {/* Purchase Invoice Routes */}
            <Route path="/purchase-invoices/detail/:id" element={<PurchaseInvoiceDetailPage />} />
            <Route path="/purchase-invoices/detail" element={<PurchaseInvoiceDetailPage />} />
            <Route path="/purchase-invoices/list" element={<PurchaseInvoicesPage />} />
            <Route
              path="/purchase-invoices"
              element={<Navigate to="/purchase-invoices/list" replace />}
            />

            {/* Credit Note Routes */}
            <Route path="/credit-notes/detail/:id" element={<CreditNoteDetailPage />} />
            <Route path="/credit-notes/new" element={<CreditNoteDetailPage />} />
            <Route path="/credit-notes/list" element={<CreditNotesPage />} />
            <Route path="/credit-notes" element={<Navigate to="/credit-notes/list" replace />} />

            {/* Debit Note Routes */}
            <Route path="/debit-notes/detail/:id" element={<DebitNoteDetailPage />} />
            <Route path="/debit-notes/new" element={<DebitNoteDetailPage />} />
            <Route path="/debit-notes/list" element={<DebitNotesPage />} />
            <Route path="/debit-notes" element={<Navigate to="/debit-notes/list" replace />} />

            {/* Offline Invoice Routes */}
            <Route path="/offline-invoices" element={<OfflineInvoicesPage />} />
            <Route path="/offline-invoices/list" element={<OfflineInvoiceListPage />} />
            <Route path="/offline-invoices/new" element={<OfflineInvoiceCreatePage />} />
            <Route path="/offline-invoices/edit/:id" element={<OfflineInvoiceCreatePage />} />

            {/* Day-End Closure Route */}
            <Route path="/day-end-closure" element={<DayEndClosurePage />} />

            {/* Field Agent Cash Route */}
            <Route path="/field-agent-cash" element={<FieldAgentCashPage />} />

            {/* Razorpay Transactions Route */}
            <Route path="/accounting/razorpay-transactions" element={<RazorpayTransactionsPage />} />

            {/* Admin Reporting Routes */}
            <Route path="/admin/reporting/parties" element={<PartiesReportPage />} />
            <Route path="/admin/reporting/delivery" element={<DeliveryMetricsPage />} />
            <Route path="/admin/reporting/customers" element={<CustomerMetricsPage />} />
            <Route path="/admin/reporting/sales" element={<SalesReportPage />} />
            <Route path="/admin/reporting/inventory" element={<InventoryReportPage />} />
            <Route path="/admin/reporting/sales-analysis" element={<SalesAnalysisPage />} />
            <Route path="/admin/reporting/winback" element={<WinbackReportPage />} />

            </Route>
          </Route>
        </Route>

        {/* Fallback - redirect to login or dashboard based on auth */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </NuqsAdapter>
    </BrowserRouter>
  )
}

export default AppRouter
